import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createPageUrl } from "utils";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  ChefHat,
  Users,
  ClipboardList,
  TrendingUp,
  Settings,
  CalendarDays,
  LogOut,
  Building2,
} from "lucide-react";
import RestaurantSwitcher from "./RestaurantSwitcher";
import CarloChat from "./CarloChat";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
  SidebarCollapseButton,
  useSidebar,
} from "components/ui/sidebar";

// Inner layout component that can use the sidebar context
function LayoutContent({ children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { collapsed } = useSidebar();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userInitial = user?.first_name?.[0] || user?.username?.[0]?.toUpperCase() || 'U';
  const userDisplayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : user?.username || '';

  const isEmployee = user?.role === 'staff';
  const isOrgAdmin = user?.role === 'org_admin';
  const allowedPages = user?.allowed_pages;

  const isPageAllowed = (slug) => {
    if (!allowedPages) return false;
    if (Array.isArray(allowedPages)) return allowedPages.includes(slug);
    return slug in allowedPages;
  };

  const filterItems = (items) =>
    isEmployee ? items.filter((item) => isPageAllowed(item.slug)) : items;

  const rawGroups = [
    {
      label: null,
      items: [
        { slug: 'dashboard', title: t("layout.dashboard"), url: createPageUrl("Dashboard"), icon: LayoutDashboard },
        { slug: 'events', title: t("layout.events"), url: createPageUrl("Events"), icon: CalendarDays },
        { slug: 'setup', title: t("layout.setup"), url: createPageUrl("Setup"), icon: Settings },
        ...(isOrgAdmin ? [{ slug: 'org-panel', title: t("layout.orgPanel"), url: '/org-panel', icon: Building2 }] : []),
      ]
    },
    {
      label: t("layout.groupFinanceSales"),
      items: [
        { slug: 'sales', title: t("layout.sales"), url: createPageUrl("Sales"), icon: ShoppingCart },
        { slug: 'finance-performance', title: t("layout.financePerformance"), url: createPageUrl("FinancePerformance"), icon: TrendingUp },
      ]
    },
    {
      label: t("layout.groupInventoryProducts"),
      items: [
        { slug: 'products', title: t("layout.products"), url: createPageUrl("Products"), icon: ChefHat },
        { slug: 'stock', title: t("layout.stock"), url: createPageUrl("Stock"), icon: Package },
      ]
    },
    {
      label: t("layout.groupOperations"),
      items: [
        { slug: 'employees', title: t("layout.employees"), url: createPageUrl("Employees"), icon: Users },
        { slug: 'tasks', title: t("layout.tasks"), url: createPageUrl("Tasks"), icon: ClipboardList },
      ]
    }
  ];

  const navigationGroups = rawGroups
    .map((group) => ({ ...group, items: filterItems(group.items) }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="h-screen flex w-full overflow-hidden" style={{ backgroundColor: '#f7f6f3' }}>
      <Sidebar className="border-r-0" style={{ backgroundColor: '#1a4731' }}>
        <SidebarCollapseButton />
        <SidebarHeader className={`border-b border-white/10 py-4 ${collapsed ? 'px-3' : 'px-6'}`}>
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            {!collapsed && (
              <div>
                <h2 className="font-bold text-lg text-white leading-tight">Carlo</h2>
                <p className="text-xs text-white/60 font-medium">{t("common.smartOperations")}</p>
              </div>
            )}
          </div>
        </SidebarHeader>

        {/* Restaurant switcher for org_admin */}
        <div className={`${collapsed ? 'px-2' : 'px-3'} pt-2`}>
          <RestaurantSwitcher collapsed={collapsed} />
        </div>

        <SidebarContent className={`py-4 ${collapsed ? 'px-2' : 'px-3'}`}>
          {navigationGroups.map((group, groupIdx) => (
            <SidebarGroup key={groupIdx} className={groupIdx > 0 ? "mt-6" : ""}>
              {group.label && (
                <SidebarGroupLabel className="text-xs font-semibold text-white/50 uppercase tracking-wider px-3 py-2 mb-1">
                  {group.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        className={`rounded-lg transition-all duration-200 ${
                          location.pathname === item.url
                            ? 'bg-white/15 text-white'
                            : 'text-white/70 hover:bg-white/10 hover:text-white'
                        }`}
                        title={collapsed ? item.title : undefined}
                      >
                        <Link to={item.url}>
                          <item.icon className={`w-5 h-5 flex-shrink-0 ${
                            location.pathname === item.url ? 'text-white' : 'text-white/50'
                          }`} />
                          {!collapsed && <span className="text-sm font-medium">{item.title}</span>}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>

        <SidebarFooter className={`border-t border-white/10 py-4 ${collapsed ? 'px-2' : 'px-4'}`}>
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-semibold text-sm">{userInitial}</span>
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white text-sm leading-tight">{userDisplayName}</p>
                <p className="text-xs text-white/60 mt-0.5 truncate">{user?.email}</p>
              </div>
            )}
            {!collapsed && (
              <button
                onClick={handleLogout}
                className="p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-900/20 transition-colors duration-200"
                title={t("login.logout")}
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
          {collapsed && (
            <button
              onClick={handleLogout}
              className="mt-2 w-full flex justify-center p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-900/20 transition-colors duration-200"
              title={t("login.logout")}
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </SidebarFooter>
      </Sidebar>

      <main className={`flex-1 flex flex-col transition-all duration-300 ${collapsed ? 'md:ml-[72px]' : 'md:ml-64'}`}>
        <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 px-6 py-4 md:hidden sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <SidebarTrigger className="hover:bg-slate-100 p-2 rounded-lg transition-colors duration-200" />
            <h1 className="text-xl font-bold text-slate-900">Carlo</h1>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6">
          {children}
        </div>
      </main>

      <CarloChat />
    </div>
  );
}

// Wrapper component that provides the sidebar context
export default function Layout({ children }) {
  return (
    <SidebarProvider>
      <style>{`
        :root {
          --primary-blue: #3b82f6;
          --primary-blue-light: #60a5fa;
          --bg-primary: #ffffff;
          --bg-secondary: #f8fafc;
          --bg-tertiary: #f1f5f9;
          --text-primary: #1e293b;
          --text-secondary: #64748b;
          --border-color: #e2e8f0;
          --shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
          --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.08);
          --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.08);
        }
      `}</style>
      <LayoutContent>{children}</LayoutContent>
    </SidebarProvider>
  );
}
